import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { isPollStoppedSaga } from "modules/polling/sagas";
import { SagaIterator } from "redux-saga";
import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { getAxiosErrorMessage } from "utils/getAxiosErrorMessage";
import { axiosInstance } from "../../axios";
import { billingActions } from "./slices";
import {
  GenerateOrgsBillingDataReportParams,
  GenerateProjectBillingDataReportParams,
  GetOrganizationBillingDataParams,
  GetOrganizationBillingDataResponse,
  GetProjectBillingBackupsLicensesDataParams,
  GetProjectBillingBackupsLicensesDataResponse,
  GetProjectBillingBackupsStorageDataParams,
  GetProjectBillingBackupsStorageDataResponse,
  GetProjectBillingDataParams,
  GetProjectBillingDataResponse,
  GetProjectBillingDatabasesDataParams,
  GetProjectBillingDatabasesDataResponse,
  GetProjectBillingInstancesDataParams,
  GetProjectBillingInstancesDataResponse,
  GetProjectBillingS3DataParams,
  GetProjectBillingS3DataResponse,
  GetProjectBillingVolumesDataParams,
  GetProjectBillingVolumesDataResponse,
  GetUserBillingDataParams,
  GetUserBillingDataResponse,
  UpdateProjectBudgetLimitParams,
  UpdateProjectBudgetLimitResponse
} from "./types";

const serviceApiPath = `gotham-wayne-tower/method/`;

export function* getUserBillingDataSaga(
  action: PayloadAction<GetUserBillingDataParams>
): SagaIterator<void> {
  try {
    const { startDate, endDate, userId } = action.payload;

    const response: AxiosResponse<GetUserBillingDataResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}billing/users/${userId}`,
      {
        params: {
          start: startDate,
          end: endDate
        }
      }
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(billingActions.getUserBillingDataSuccess(response.data));
    }
  } catch (e) {
    yield put(
      billingActions.getUserBillingDataFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get user billing data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getOrganizationBillingDataSaga(
  action: PayloadAction<GetOrganizationBillingDataParams>
): SagaIterator<void> {
  try {
    const { startDate, endDate, id } = action.payload;
    const response: AxiosResponse<GetOrganizationBillingDataResponse> =
      yield call(
        axiosInstance.get,
        `${serviceApiPath}billing/organizations/${id}`,
        {
          params: {
            start: startDate,
            end: endDate
          }
        }
      );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(
        billingActions.getOrganizationBillingDataSuccess(response.data)
      );
    }
  } catch (e) {
    yield put(
      billingActions.getOrganizationBillingDataFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get organization billing data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getProjectBillingDataSaga(
  action: PayloadAction<GetProjectBillingDataParams>
): SagaIterator<void> {
  try {
    const { regionId, orgId, id } = action.payload;
    const response: AxiosResponse<GetProjectBillingDataResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}billing/organizations/${orgId}/regions/${regionId}/projects/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(billingActions.getProjectBillingDataSuccess(response.data));
    }
  } catch (e) {
    yield put(
      billingActions.getProjectBillingDataFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get project billing data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getProjectBillingInstancesDataSaga(
  action: PayloadAction<GetProjectBillingInstancesDataParams>
): SagaIterator<void> {
  try {
    const { startDate, endDate, organizationId, projectId, regionId } =
      action.payload;
    const response: AxiosResponse<GetProjectBillingInstancesDataResponse> =
      yield call(
        axiosInstance.get,
        `${serviceApiPath}billing/organizations/${organizationId}/regions/${regionId}/projects/${projectId}/instances`,
        {
          params: {
            start: startDate,
            end: endDate
          }
        }
      );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(
        billingActions.getProjectBillingInstancesDataSuccess(response.data)
      );
    }
  } catch (e) {
    yield put(
      billingActions.getProjectBillingInstancesDataFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get project instances billing data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getProjectBillingVolumesDataSaga(
  action: PayloadAction<GetProjectBillingVolumesDataParams>
): SagaIterator<void> {
  try {
    const { startDate, endDate, organizationId, projectId, regionId } =
      action.payload;
    const response: AxiosResponse<GetProjectBillingVolumesDataResponse> =
      yield call(
        axiosInstance.get,
        `${serviceApiPath}billing/organizations/${organizationId}/regions/${regionId}/projects/${projectId}/volumes`,
        {
          params: {
            start: startDate,
            end: endDate
          }
        }
      );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(
        billingActions.getProjectBillingVolumesDataSuccess(response.data)
      );
    }
  } catch (e) {
    yield put(
      billingActions.getProjectBillingVolumesDataFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get project volumes billing data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getProjectBillingDatabasesDataSaga(
  action: PayloadAction<GetProjectBillingDatabasesDataParams>
): SagaIterator<void> {
  try {
    const { startDate, endDate, organizationId, projectId, regionId } =
      action.payload;
    const response: AxiosResponse<GetProjectBillingDatabasesDataResponse> =
      yield call(
        axiosInstance.get,
        `${serviceApiPath}billing/organizations/${organizationId}/regions/${regionId}/projects/${projectId}/databases`,
        {
          params: {
            start: startDate,
            end: endDate
          }
        }
      );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(
        billingActions.getProjectBillingDatabasesDataSuccess(response.data)
      );
    }
  } catch (e) {
    yield put(
      billingActions.getProjectBillingDatabasesDataFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get project databases billing data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getProjectBillingS3DataSaga(
  action: PayloadAction<GetProjectBillingS3DataParams>
): SagaIterator<void> {
  try {
    const { startDate, endDate, organizationId, projectId, regionId } =
      action.payload;
    const response: AxiosResponse<GetProjectBillingS3DataResponse> = yield call(
      axiosInstance.get,
      `${serviceApiPath}billing/organizations/${organizationId}/regions/${regionId}/projects/${projectId}/s3`,
      {
        params: {
          start: startDate,
          end: endDate
        }
      }
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(billingActions.getProjectBillingS3DataSuccess(response.data));
    }
  } catch (e) {
    yield put(
      billingActions.getProjectBillingS3DataFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get project Object Storage billing data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getProjectBillingBackupsStorageDataSaga(
  action: PayloadAction<GetProjectBillingBackupsStorageDataParams>
): SagaIterator<void> {
  try {
    const { startDate, endDate, organizationId, projectId, regionId } =
      action.payload;
    const response: AxiosResponse<GetProjectBillingBackupsStorageDataResponse> =
      yield call(
        axiosInstance.get,
        `${serviceApiPath}billing/organizations/${organizationId}/regions/${regionId}/projects/${projectId}/backup-storage`,
        {
          params: {
            start: startDate,
            end: endDate
          }
        }
      );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(
        billingActions.getProjectBillingBackupsStorageDataSuccess(response.data)
      );
    }
  } catch (e) {
    yield put(
      billingActions.getProjectBillingBackupsStorageDataFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get project backups storage billing data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getProjectBillingBackupsLicensesDataSaga(
  action: PayloadAction<GetProjectBillingBackupsLicensesDataParams>
): SagaIterator<void> {
  try {
    const { startDate, endDate, organizationId, projectId, regionId } =
      action.payload;
    const response: AxiosResponse<GetProjectBillingBackupsLicensesDataResponse> =
      yield call(
        axiosInstance.get,
        `${serviceApiPath}billing/organizations/${organizationId}/regions/${regionId}/projects/${projectId}/backup-licenses`,
        {
          params: {
            start: startDate,
            end: endDate
          }
        }
      );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(
        billingActions.getProjectBillingBackupsLicensesDataSuccess(
          response.data
        )
      );
    }
  } catch (e) {
    yield put(
      billingActions.getProjectBillingBackupsLicensesDataFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get project backups licenses billing data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* generateProjectBillingDataReportSaga(
  action: PayloadAction<GenerateProjectBillingDataReportParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to generate report...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { startDate, endDate, regionId, organizationId, projectId } =
      action.payload;
    yield call(
      axiosInstance.post,
      `${serviceApiPath}billing/organizations/${organizationId}/regions/${regionId}/projects/${projectId}?start=${startDate}&end=${endDate}`
    );
    yield put(
      notificationsActions.showNotification({
        title: "Request to generate report has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
    yield put(billingActions.generateProjectBillingDataReportSuccess());
  } catch (e) {
    yield put(
      billingActions.generateProjectBillingDataReportFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to generate report",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* generateOrgsBillingDataReportSaga(
  action: PayloadAction<GenerateOrgsBillingDataReportParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to generate billing report...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { startDate, endDate, userId } = action.payload;
    yield call(
      axiosInstance.post,
      `${serviceApiPath}billing/users/${userId}/download-report?start=${startDate}&end=${endDate}`
    );
    yield put(
      notificationsActions.showNotification({
        title: "Request to generate billing report has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
    yield put(billingActions.generateOrgsBillingDataReportSuccess());
  } catch (e) {
    yield put(
      billingActions.generateOrgsBillingDataReportFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to generate billing report",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateProjectBudgetLimitSaga(
  action: PayloadAction<UpdateProjectBudgetLimitParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating the project budget limit...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, organizationId, id, data } = action.payload;
    const response: AxiosResponse<UpdateProjectBudgetLimitResponse> =
      yield call(
        axiosInstance.put,
        `${serviceApiPath}billing/organizations/${organizationId}/regions/${regionId}/projects/${id}/budget`,
        data
      );
    yield put(billingActions.updateProjectBudgetLimitSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Project budget limit has been successfully updated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      billingActions.updateProjectBudgetLimitFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update project budget limit",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* watcherSaga(): SagaIterator<void> {
  yield all([
    takeLatest(
      billingActions.getUserBillingDataRequest,
      getUserBillingDataSaga
    ),
    takeLatest(
      billingActions.getOrganizationBillingDataRequest,
      getOrganizationBillingDataSaga
    ),
    takeLatest(
      billingActions.getProjectBillingDataRequest,
      getProjectBillingDataSaga
    ),
    takeLatest(
      billingActions.getProjectBillingInstancesDataRequest,
      getProjectBillingInstancesDataSaga
    ),
    takeLatest(
      billingActions.getProjectBillingVolumesDataRequest,
      getProjectBillingVolumesDataSaga
    ),
    takeLatest(
      billingActions.getProjectBillingDatabasesDataRequest,
      getProjectBillingDatabasesDataSaga
    ),
    takeLatest(
      billingActions.getProjectBillingS3DataRequest,
      getProjectBillingS3DataSaga
    ),
    takeLatest(
      billingActions.getProjectBillingBackupsStorageDataRequest,
      getProjectBillingBackupsStorageDataSaga
    ),
    takeLatest(
      billingActions.getProjectBillingBackupsLicensesDataRequest,
      getProjectBillingBackupsLicensesDataSaga
    ),
    takeEvery(
      billingActions.generateProjectBillingDataReportRequest,
      generateProjectBillingDataReportSaga
    ),
    takeEvery(
      billingActions.generateOrgsBillingDataReportRequest,
      generateOrgsBillingDataReportSaga
    ),
    takeEvery(
      billingActions.updateProjectBudgetLimitRequest,
      updateProjectBudgetLimitSaga
    )
  ]);
}
